/* Reference reviews: local filtering and a preview-only submission form. */
(() => {
  if (customElements.get('pr-reviews')) return;
  customElements.define('pr-reviews', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.filter = 'all';
      this.urls = new Set();
      this.observer = new MutationObserver(records => {
        const structural = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches('.rv-card,.rv-grid,[data-rv-strings]') || node.querySelector('.rv-card,.rv-grid,[data-rv-strings]'))
        ));
        if (structural) this.scheduleRefresh();
      });
      this.observe();
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('change', event => this.onChange(event), { signal });
      this.addEventListener('input', event => this.onInput(event), { signal });
      this.addEventListener('submit', event => {
        if (event.target === this.part('rvmForm')) this.submitPreview(event);
      }, { signal });
      document.addEventListener('keydown', event => this.onKey(event), { signal, capture: true });
      document.addEventListener('shopify:block:select', event => {
        const card = event.target.closest?.('.rv-card');
        if (!card || !this.contains(card)) return;
        this.filter = 'all';
        this.limit = this.cards.length;
        this.render();
      }, { signal });
      window.addEventListener('resize', () => this.scheduleClamps(), { signal, passive: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.close(false);
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.refreshFrame);
      cancelAnimationFrame(this.clampFrame);
      this.clearPhotos();
    }

    part(id) { return this.querySelector(`[data-rv-id="${id}"]`); }
    observe() { this.observer.observe(this, { childList: true, subtree: true }); }
    scheduleRefresh() {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    }
    refresh() {
      const config = this.querySelector('[data-rv-strings]');
      const grid = this.part('rvList');
      if (!grid || !config || !this.part('rvmForm')) return;
      try { this.labels = JSON.parse(config.textContent); } catch { return; }
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 4);
      this.limit = this.limit || this.pageSize;
      this.cards = [...grid.querySelectorAll(':scope > .rv-card')];
      this.render();
    }
    render() {
      if (!this.cards) return;
      const number = (card, key) => Number(card.dataset[key]) || 0;
      const sort = this.part('rvSort').value;
      const cards = this.cards.filter(card => {
        if (this.filter === 'photo') return card.dataset.photo === '1';
        if (this.filter === 'low') return number(card, 'stars') <= 3;
        return this.filter === 'all' || number(card, 'stars') === Number(this.filter);
      }).sort((a, b) => {
        if (sort === 'new') return number(b, 'date') - number(a, 'date');
        if (sort === 'hi') return number(b, 'stars') - number(a, 'stars') || number(b, 'help') - number(a, 'help');
        if (sort === 'lo') return number(a, 'stars') - number(b, 'stars') || number(b, 'help') - number(a, 'help');
        return number(b, 'help') - number(a, 'help');
      });
      // Keep keyboard order aligned with visual order without observing our own moves.
      this.observer.disconnect();
      this.cards.forEach(card => { card.hidden = true; });
      const grid = this.part('rvList');
      cards.forEach((card, index) => { grid.append(card); card.hidden = index >= this.limit; });
      this.observe();
      this.querySelectorAll('.rv-f').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.f === this.filter)));
      this.part('rvEmpty').hidden = cards.length > 0;
      this.part('rvMore').hidden = cards.length <= this.limit;
      this.part('rvCount').textContent = cards.length ? this.labels.showing
        .replace('{shown}', Math.min(cards.length, this.limit))
        .replace('{total}', this.filter === 'all' ? this.dataset.total || cards.length : cards.length) : '';
      this.scheduleClamps();
    }
    scheduleClamps() {
      cancelAnimationFrame(this.clampFrame);
      this.clampFrame = requestAnimationFrame(() => this.clamps());
    }
    clamps() {
      const mobile = window.matchMedia('(max-width:640px)').matches;
      this.cards?.forEach(card => {
        const body = card.querySelector('[data-rv-body]');
        const button = card.querySelector('[data-rv-expand]');
        if (!body || !button || card.hidden) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        body.classList.toggle('rvc-clamp', mobile && !expanded);
        button.hidden = !mobile || (!expanded && body.scrollHeight <= body.clientHeight + 1);
      });
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (event.target === this.part('rvmOv')) { this.close(); return; }
      if (!button || !this.contains(button)) return;
      if (button.matches('.rv-f')) { this.filter = button.dataset.f; this.limit = this.pageSize; this.render(); }
      if (button === this.part('rvMore')) { this.limit += this.pageSize; this.render(); }
      if (button === this.part('rvWrite')) this.open(button);
      if (button === this.part('rvmX') || button === this.part('rvmBack')) this.close();
      if (button.matches('[data-rv-expand]')) {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? this.labels.show_less : this.labels.read_more;
        this.clamps();
      }
      if (button.matches('[data-rv-remove-photo]')) {
        const thumb = button.closest('.rvm-th');
        URL.revokeObjectURL(thumb.dataset.url);
        this.urls.delete(thumb.dataset.url);
        thumb.remove();
        this.part('rvmAdd').hidden = false;
        this.part('rvmFile').focus();
      }
      if (button.matches('[data-rv-buy]')) document.querySelector('[data-pr-add-to-cart]:not([data-pr-sticky-button])')?.click();
    }
    onChange(event) {
      if (event.target === this.part('rvSort')) { this.limit = this.pageSize; this.render(); }
      if (event.target.matches('input[name="rate"]')) {
        this.part('rvmRL').textContent = this.labels[['poor', 'fair', 'good', 'very_good', 'excellent'][Number(event.target.value) - 1]];
        this.error('eRate', 'rvmRateBox', '');
      }
      if (event.target === this.part('rvmOk') && event.target.checked) this.error('eOk', 'rvmOk', '');
      if (event.target === this.part('rvmFile')) this.addPhotos(event.target);
    }
    onInput(event) {
      if (event.target === this.part('rvmText')) {
        this.part('rvmCnt').textContent = `${event.target.value.length} / 1000`;
        if (event.target.value.trim().length >= 10) this.error('eText', 'rvmText', '');
      }
      if (event.target === this.part('rvmName') && event.target.value.trim()) this.error('eName', 'rvmName', '');
      if (event.target === this.part('rvmMail')) this.error('eMail', 'rvmMail', '');
    }
    error(id, field, text) {
      this.part(id).textContent = text;
      if (text) this.part(field)?.setAttribute('aria-invalid', 'true');
      else this.part(field)?.removeAttribute('aria-invalid');
    }
    open(opener) {
      this.opener = opener;
      const form = this.part('rvmForm');
      form.reset();
      form.hidden = false;
      this.part('rvmDone').hidden = true;
      this.clearPhotos();
      this.part('rvmAdd').hidden = false;
      this.part('rvmRL').textContent = this.labels.tap_star;
      this.part('rvmCnt').textContent = '0 / 1000';
      this.querySelectorAll('.rvm-err').forEach(error => { error.textContent = ''; });
      this.querySelectorAll('[aria-invalid]').forEach(field => field.removeAttribute('aria-invalid'));
      this.querySelector('.rvm-body').scrollTop = 0;
      this.part('rvmOv').classList.add('open');
      this.part('rvmOv').setAttribute('aria-hidden', 'false');
      document.body.classList.add('rvm-lock');
      this.part('rvm').focus();
    }
    close(restoreFocus = true) {
      const overlay = this.part('rvmOv');
      if (!overlay?.classList.contains('open')) return;
      overlay.classList.remove('open');
      overlay.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('pr-reviews .rvm-ov.open')) document.body.classList.remove('rvm-lock');
      if (restoreFocus && this.opener?.isConnected) this.opener.focus();
      this.clearPhotos();
    }
    onKey(event) {
      if (!this.part('rvmOv')?.classList.contains('open')) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const dialog = this.part('rvm');
      const focusable = [...dialog.querySelectorAll('button,input,select,textarea,a[href]')]
        .filter(node => !node.disabled && !node.closest('[hidden]') && getComputedStyle(node).visibility !== 'hidden');
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    clearPhotos() {
      this.urls?.forEach(url => URL.revokeObjectURL(url));
      this.urls?.clear();
      this.querySelectorAll('.rvm-th').forEach(thumb => thumb.remove());
    }
    addPhotos(input) {
      const add = this.part('rvmAdd');
      for (const file of input.files) {
        if (this.urls.size >= 3) break;
        if (!file.type.startsWith('image/')) continue;
        const url = URL.createObjectURL(file);
        this.urls.add(url);
        const thumb = document.createElement('div');
        thumb.className = 'rvm-th';
        thumb.dataset.url = url;
        const img = document.createElement('img');
        img.src = url;
        img.alt = this.labels.photo_alt.replace('{number}', this.urls.size);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.rvRemovePhoto = '';
        remove.setAttribute('aria-label', this.labels.remove_photo);
        remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
        thumb.append(img, remove);
        add.before(thumb);
      }
      input.value = '';
      add.hidden = this.urls.size >= 3;
    }
    submitPreview(event) {
      event.preventDefault();
      const form = this.part('rvmForm');
      let firstInvalid;
      const check = (valid, error, field, label, focus) => {
        this.error(error, field, valid ? '' : this.labels[label]);
        if (!valid && !firstInvalid) firstInvalid = focus || this.part(field);
      };
      check(form.querySelector('input[name="rate"]:checked'), 'eRate', 'rvmRateBox', 'rating_error', form.querySelector('input[name="rate"]'));
      check(this.part('rvmText').value.trim().length >= 10, 'eText', 'rvmText', 'text_error');
      check(this.part('rvmName').value.trim(), 'eName', 'rvmName', 'name_error');
      const email = this.part('rvmMail');
      check(email.value.trim() && email.checkValidity(), 'eMail', 'rvmMail', 'email_error');
      check(this.part('rvmOk').checked, 'eOk', 'rvmOk', 'consent_error');
      if (firstInvalid) { firstInvalid.focus(); firstInvalid.scrollIntoView?.({ block: 'center' }); return; }
      // Intentionally no request, storage, order lookup, or review-app submission.
      this.part('rvmDoneN').textContent = `, ${this.part('rvmName').value.trim()}`;
      form.hidden = true;
      this.part('rvmDone').hidden = false;
      this.part('rvmDone').scrollTop = 0;
      this.part('rvmDoneT').focus();
    }
  });
})();
